import type { MailFolderTableQuery, TableView } from "owa-mail-list-store";
import { type SortColumn, type TableQueryType } from "owa-mail-list-store";
import { isRecipientSearchSortOrder, isSenderSearchSortOrder, isCategorySearchSortOrder, isImportanceSearchSortOrder, type ClientSearchSortOrder, type SearchTableQuery, } from "owa-mail-list-search";
import type SortDirection from "owa-service/lib/contract/SortDirection";

export interface JumpToSortInfo {
    sortColumn: SortColumn;
    sortDirection: SortDirection;
    /**
     * Set when every result is already loaded and sorted on the client, so
     * Jump To moves to a loaded row instead of reloading the table from the server.
     */
    clientSortOrder?: ClientSearchSortOrder;
}

/**
 * Gets the sort Jump To works against. Folder tables use their sort; search tables only support
 * Jump To when sorted by sender, recipient, category, or importance, which map onto From, To,
 * Category, and Importance.
 */
export default function getJumpToSortInfo(tableView: TableView | undefined): JumpToSortInfo | undefined {
    const tableQuery = tableView?.tableQuery;
    if (!tableQuery) {
        return undefined;
    }
    
    if (tableQuery.type === 1) {
        const searchSortOrder = (tableQuery as SearchTableQuery).searchSortOrder;
        if (isImportanceSearchSortOrder(searchSortOrder)) {
            return {
                sortColumn: 5,
                sortDirection: searchSortOrder === "ImportanceAscending" ? "Ascending" : "Descending",
                clientSortOrder: searchSortOrder,
            };
        }
        if (isCategorySearchSortOrder(searchSortOrder)) {
            return {
                sortColumn: 14,
                sortDirection: searchSortOrder === "CategoryDescending" ? "Descending" : "Ascending",
                clientSortOrder: searchSortOrder,
            };
        }
        return isSenderSearchSortOrder(searchSortOrder) ||
            isRecipientSearchSortOrder(searchSortOrder)
            ? {
                sortColumn: isSenderSearchSortOrder(searchSortOrder)
                    ? 3 : 9,
                sortDirection: searchSortOrder === "SenderDescending" ||
                    searchSortOrder === "RecipientDescending"
                    ? "Descending"
                    : "Ascending",
                clientSortOrder: searchSortOrder,
            }
            : undefined;
    }
    
    return (tableQuery as MailFolderTableQuery).sortBy;
}
